import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import PencilButton from 'components/Button/PencilButton'
import { InfoBoxRow } from 'components/InfoBox'
import { FlexDivRowCentered } from 'components/layout/flex'
import Spacer from 'components/Spacer'
import { Body, NumericValue } from 'components/Text'
import useWindowSize from 'hooks/useWindowSize'
import { setOpenModal, setOpenModalWithParams } from 'state/app/reducer'
import { CrossMarginRiskStatus } from 'state/futures/common/types'
import {
	selectCrossMarginAccount,
	selectCrossMarginMarginInfo,
	selectKeeperUSDBalance,
	selectMaintenanceMarginRatio,
} from 'state/futures/snxPerpsV3/selectors'
import { ManageModalType } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'

const TradeBalanceCrossMargin: React.FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { deviceType } = useWindowSize()
	const { ratio, riskStatus } = useAppSelector(selectMaintenanceMarginRatio)
	const account = useAppSelector(selectCrossMarginAccount)
	const keeperBalance = useAppSelector(selectKeeperUSDBalance)

	const { requiredMaintenanceMargin, availableMargin } = useAppSelector(selectCrossMarginMarginInfo)

	return (
		<Container mobile={deviceType === 'mobile'}>
			<Header>
				<Body weight="bold" size="large">
					{t('futures.market.trade.margin.account')}
				</Body>
				{!!account && (
					<TransactionButton
						variant="yellow"
						size="xsmall"
						textTransform="none"
						onClick={() => dispatch(setOpenModal('futures_deposit_withdraw_cross_margin'))}
					>
						{t('futures.market.trade.trade-balance.manage-button')}
					</TransactionButton>
				)}
			</Header>
			<Spacer height={12} />
			<Header>
				<Body color="secondary">{t('futures.market.trade.margin.margin-ratio')}</Body>
				<FlexDivRowCentered>
					<NumericValue
						weight="bold"
						fontSize={16}
						color={
							riskStatus === 'high' ? 'negative' : riskStatus === 'low' ? 'positive' : 'preview'
						}
					>
						{formatPercent(ratio, { maxDecimals: 2 })}
					</NumericValue>
					<Spacer width={7} />
					<HealthIndicator>
						<AccountHealthIndicator risk={riskStatus} />
					</HealthIndicator>
				</FlexDivRowCentered>
			</Header>
			<MarginInfoSection>
				<InfoBoxRow
					title={t('futures.market.trade.trade-balance.maintenance-margin')}
					textValue={formatDollars(requiredMaintenanceMargin)}
				/>
				<InfoBoxRow
					title={t('futures.market.trade.trade-balance.account-equity')}
					textValue={formatDollars(availableMargin)}
				/>
				<KeeperContainer>
					<InfoBoxRow
						title="Keeper Balance"
						textValue={formatDollars(keeperBalance)}
						textValueIcon={
							<PencilButton
								width={10}
								height={10}
								onClick={() =>
									dispatch(
										setOpenModalWithParams({
											type: 'futures_deposit_withdraw_cross_margin',
											params: { tab: ManageModalType.Orders },
										})
									)
								}
								style={{ cursor: 'pointer', marginLeft: '10px' }}
							/>
						}
					/>
				</KeeperContainer>
			</MarginInfoSection>
		</Container>
	)
})

const KeeperContainer = styled.div`
	margin-top: 15px;
	padding-top: 15px;
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
`

const Container = styled.div<{ mobile?: boolean }>`
	width: 100%;
	padding: 15px 15px;
	border-bottom: ${(props) => (props.mobile ? props.theme.colors.selectedTheme.border : 0)};
`

const Header = styled(FlexDivRowCentered)``

const MarginInfoSection = styled.div`
	padding-top: 5px;
`

const AccountHealthIndicator = ({ risk }: { risk: CrossMarginRiskStatus }) => {
	return (
		<HealthBarContainer>
			<HealthBar $risk={risk} $height={5} />
			<HealthBar
				$risk={
					risk === CrossMarginRiskStatus.Moderate || risk === CrossMarginRiskStatus.High
						? risk
						: undefined
				}
				$height={9}
			/>
			<HealthBar $risk={risk === CrossMarginRiskStatus.High ? risk : undefined} $height={13} />
		</HealthBarContainer>
	)
}

const HealthIndicator = styled.div`
	margin-bottom: 2px;
`

const HealthBarContainer = styled.div`
	display: flex;
	flex-direction: row;
	gap: 2px;
	align-items: flex-end;
`

const HealthBar = styled.div<{ $height: number; $risk?: CrossMarginRiskStatus }>`
	background-color: ${(props) =>
		props.$risk
			? props.theme.colors.selectedTheme.newTheme.riskStatus[props.$risk]
			: props.theme.colors.selectedTheme.newTheme.border.color};
	height: ${(props) => props.$height}px;
	width: 3px;
	border-radius: 1px;
`

export default TradeBalanceCrossMargin
